import { useCallback, useState } from 'react';
import { useDebounce } from 'react-use';

interface UseDebouncedValueOptions<T> {
  /** Should return true if the value is valid for the given situation */
  canUpdateValue: (value: T) => boolean;

  /** If this is present, it will replace the value if {@link canUpdateValue} returns false */
  clearValue?: T;

  /** How long to wait before replacing the value */
  delay: number;
}

export interface UseDebouncedValueResult<T> {
  /** The debounced value. This should lag behind the initial value by the delay. */
  value: T;

  /** Bypass the delay and immediately set a value */
  setValue(value: T): void;
}

/**
 * This hook initially returns the value supplied via props.
 * Then, if that value changes, its output only changes after the supplied delay.
 * If it changes multiple times within that window, only the last value will be seen.
 * @param value The initial value
 * @param options Configuration
 * @returns An {@link UseDebouncedValueResult} object
 */
export default function useDebouncedValue<T>(
  value: T,
  options: UseDebouncedValueOptions<T>
): UseDebouncedValueResult<T> {
  const [result, setResult] = useState<T>(value);
  const { canUpdateValue, delay } = options;

  /**
   * Sets the clear value as the result, if it exists.
   */
  function maybeSetClearValue(): void {
    // We don't want to do a normal options?.clearValue or isNullOrUndefined here, because `null` might be a valid clearValue to use.
    if ('clearValue' in options) {
      setResult(options.clearValue);
    }
  }

  useDebounce(
    () => {
      if (canUpdateValue(value)) {
        setResult(value);
      } else {
        maybeSetClearValue();
      }
    },
    delay,
    [value]
  );

  const setValue = useCallback(newValue => {
    // Wrapping this in a promise prevents batching and ensures the value is set immediately. Note that tests will fail if this is removed.

    // eslint-disable-next-line promise/catch-or-return
    Promise.resolve()
      // eslint-disable-next-line promise/always-return
      .then(() => {
        setResult(newValue);
      });
  }, []);

  return {
    value: result,
    setValue
  };
}
